import { Profile } from 'oidc-client';
import { translations } from '../../config/translations';
import { TransferStatusEnum, ToolTransferVm } from '../../utils/api';

/** Grey while drafted, orange on the road, green once received - same on every screen. */
export const transferStatusColor = (status: TransferStatusEnum): string => {
    switch (status) {
        case TransferStatusEnum.Shipped:
            return 'orange';
        case TransferStatusEnum.Received:
            return 'green';
        default:
            return 'default';
    }
};

export const transferStatusLabel = (status: TransferStatusEnum): string => {
    switch (status) {
        case TransferStatusEnum.Draft:
            return 'U izradi';
        case TransferStatusEnum.Shipped:
            return 'Otpremljeno';
        case TransferStatusEnum.Received:
            return 'Zaprimljeno';
        default:
            return '';
    }
};

/**
 * A site manager may act on the source side of a transfer (create, edit, ship, return, delete)
 * only for warehouses he runs; SuperAdmin, Administrator and Skladiste may act on any. The server enforces this
 * for real - this is only about which buttons to show.
 */
export const isSourceSide = (
    transfer: ToolTransferVm,
    isUnrestricted: boolean,
    siteManagerProjects: string[]
): boolean =>
    isUnrestricted ||
    (transfer.fromWarehouseProjectId != null &&
        siteManagerProjects.includes(transfer.fromWarehouseProjectId.toString()));

/**
 * Same idea for the destination side, which is what grants the right to receive. A person has no
 * login, so a hand-over to a person is confirmed by the site manager of the source warehouse.
 */
export const isDestinationSide = (
    transfer: ToolTransferVm,
    isUnrestricted: boolean,
    siteManagerProjects: string[]
): boolean =>
    isUnrestricted ||
    (transfer.toWarehouseProjectId != null &&
        siteManagerProjects.includes(transfer.toWarehouseProjectId.toString())) ||
    (transfer.toEmployeeId != null && isSourceSide(transfer, false, siteManagerProjects));

/**
 * A transfer end or tool location is a warehouse or a person. Selects carry it as one string key
 * ("w:12" / "e:5") so both kinds can live in a single dropdown.
 */
export interface TransferLocation {
    warehouseId?: number;
    employeeId?: number;
}

export const locationKey = (
    warehouseId?: number | null,
    employeeId?: number | null
): string | undefined =>
    warehouseId != null ? `w:${warehouseId}` : employeeId != null ? `e:${employeeId}` : undefined;

export const parseLocationKey = (key?: string | null): TransferLocation => {
    const [kind, id] = (key || '').split(':');
    const value = parseInt(id);

    if (isNaN(value)) return {};

    return kind === 'w' ? { warehouseId: value } : kind === 'e' ? { employeeId: value } : {};
};

/** Tool label used in pickers and lists: "12. VT PUMPA ...". */
export const toolLabel = (tool: { number: number; name: string }): string =>
    `${tool.number}. ${tool.name}`;

/**
 * The site manager's project ids from the OIDC profile. A claim that occurs once arrives as a plain
 * string, not an array, and `string.includes` would then match substrings ("14" contains "4").
 */
export const getSiteManagerProjects = (userProfile: Profile): string[] => {
    const value = userProfile['siteManagerProjects'] as string | string[] | undefined;

    if (value == null) return [];

    return Array.isArray(value) ? value : [value];
};

export const toolNames = (transfer: ToolTransferVm): string =>
    (transfer.items || []).map((i) => i.toolName).join(', ') || translations.general.noData;

/** Fired after a transfer changes status, so the menu badge recounts without waiting for its poll. */
export const TRANSFERS_CHANGED_EVENT = 'spegra:transfers-changed';

export const notifyTransfersChanged = (): void => {
    window.dispatchEvent(new Event(TRANSFERS_CHANGED_EVENT));
};
